import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DecimalInput } from "@/components/DecimalInput";
import { AlertTriangle, ArrowRight } from "lucide-react";

const WEIGH = { kg: 1000, kilogram: 1000, l: 1000, lt: 1000, litr: 1000, g: 1, gram: 1, ml: 1, dkg: 10 };
export const gramsOf = (unit, qty) => {
  const f = WEIGH[(unit || "").trim().toLowerCase()];
  return f == null || qty == null ? null : qty * f;
};

const fmtG = (n) => new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 0 }).format(n || 0);

export function ScaleWeightDialog({ open, onOpenChange, currentWeight, currentPortions, sections, items, onApply }) {
  const [pg, setPg] = useState(currentWeight || 0);
  const [portions, setPortions] = useState(currentPortions || 1);
  const [mode, setMode] = useState("all");
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (open) {
      setPg(currentWeight || 0);
      setPortions(currentPortions || 1);
      setMode("all");
      setSelected({});
    }
  }, [open, currentWeight, currentPortions]);

  const totalOld = (Number(currentWeight) || 0) * (Number(currentPortions) || 0);
  const totalNew = (Number(pg) || 0) * (Number(portions) || 0);
  const ksItems = items.filter((it) => gramsOf(it.unit, it.quantity) == null);
  const selIds = Object.keys(selected).filter((k) => selected[k]);

  const apply = () => {
    const newW = Number(pg) || 0;
    const newP = Number(portions) || 0;
    if (newW <= 0 || newP <= 0) { toast.error("Zadejte kladnou gramáž porce i počet porcí"); return; }

    let newItems;
    if (mode === "all") {
      if (totalOld <= 0) { onApply(items, newW, newP); onOpenChange(false); return; }
      const factor = totalNew / totalOld;
      newItems = items.map((it) =>
        gramsOf(it.unit, it.quantity) == null ? it : { ...it, quantity: +(it.quantity * factor).toFixed(4) });
    } else {
      if (selIds.length === 0) { toast.error("Vyberte alespoň jednu sekci"); return; }
      const inSel = (it) => selIds.includes(it.section_id);
      const selWeight = items.filter(inSel).reduce((a, it) => a + (gramsOf(it.unit, it.quantity) || 0), 0);
      const delta = totalNew - totalOld;
      if (selWeight <= 0) { toast.error("Vybrané sekce nemají vážitelné suroviny (kg/l/g/ml)"); return; }
      const factor = (selWeight + delta) / selWeight;
      if (factor < 0) { toast.error("Snížení je větší než hmotnost vybraných sekcí"); return; }
      newItems = items.map((it) =>
        (inSel(it) && gramsOf(it.unit, it.quantity) != null) ? { ...it, quantity: +(it.quantity * factor).toFixed(4) } : it);
    }
    onApply(newItems, newW, newP);
    onOpenChange(false);
    toast.success("Suroviny přepočítány dle gramáže a porcí");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Přepočet surovin dle porcí</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Gramáž porce (g)</Label>
              <DecimalInput data-testid="scale-target" value={pg} onValueChange={setPg} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Počet porcí</Label>
              <DecimalInput data-testid="scale-portions" value={portions} onValueChange={setPortions} className="mt-1.5" />
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 text-sm bg-muted/40 rounded-lg py-2" data-testid="scale-total-preview">
            <span className="text-muted-foreground">Celkem {fmtG(totalOld)} g</span>
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="font-semibold num">{fmtG(totalNew)} g</span>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="scale-mode-all">
              <Checkbox checked={mode === "all"} onCheckedChange={() => setMode("all")} />
              Poměrově mezi všechny suroviny
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="scale-mode-sections">
              <Checkbox checked={mode === "sections"} onCheckedChange={() => setMode("sections")} />
              Jen do vybraných sekcí
            </label>
          </div>
          {mode === "sections" && (
            <div className="pl-6 space-y-1.5 max-h-40 overflow-auto">
              {sections.length === 0 && <p className="text-xs text-muted-foreground">Bez sekcí.</p>}
              {sections.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`scale-sec-${s.id}`}>
                  <Checkbox checked={!!selected[s.id]} onCheckedChange={() => setSelected((p) => ({ ...p, [s.id]: !p[s.id] }))} />
                  {s.name}
                </label>
              ))}
            </div>
          )}
          {ksItems.length > 0 && (
            <div className="text-[11px] text-secondary flex items-start gap-1 bg-secondary/5 border border-secondary/20 rounded-lg px-3 py-2" data-testid="scale-ks-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>Suroviny v „ks" nelze navyšovat – zůstanou beze změny: {ksItems.map((i) => i.material_name).join(", ")}</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Zrušit</Button>
          <Button onClick={apply} data-testid="scale-apply">Přepočítat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
